import { useEffect, useMemo, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import {
  ArrowLeft, Camera, Check, ChevronRight, Clock3, FileText, Image as ImageIcon,
  MapPin, Minus, Package, Pill, Plus, RefreshCw, Search, ShoppingBag, Sparkles,
  Truck, Upload, X, Navigation, AlertCircle,
} from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';
import { uploadFile } from '@/lib/uploads';
import { openRazorpayCheckout } from '@/lib/razorpay';
import MobileShell from '@/components/MobileShell';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import ProviderTrackingMap from '@/components/map/ProviderTrackingMap';

const STATUS = {
  payment_pending: 'Payment pending',
  payment_failed: 'Payment failed',
  prescription_uploaded: 'Prescription received',
  paid: 'Finding pharmacy',
  searching_pharmacy: 'Finding pharmacy',
  pharmacy_accepted: 'Pharmacy reviewing',
  pharmacy_confirmed: 'Pharmacy confirmed',
  preparing: 'Preparing',
  ready_for_pickup: 'Ready for pickup',
  ranger_searching: 'Finding Ranger',
  ranger_assigned: 'Ranger assigned',
  ranger_arriving_pickup: 'Ranger heading to pharmacy',
  ready_for_handover: 'Ranger at pharmacy',
  picked_up: 'Ranger has your order',
  ranger_arriving_customer: 'Ranger heading to you',
  out_for_delivery: 'Out for delivery',
  delivered: 'Delivered',
  completed: 'Delivered',
  pharmacy_unavailable: 'No nearby pharmacy available',
  delivery_failed: 'No Ranger available',
  cancelled: 'Cancelled',
  refunded: 'Refunded',
};

const STEPS = ['searching_pharmacy','pharmacy_accepted','pharmacy_confirmed','preparing','ready_for_pickup','ranger_assigned','picked_up','out_for_delivery','completed'];

const money = (v) => Number(v || 0).toFixed(2);

export default function ConsumerPharmacy() {
  const cameraRef = useRef(null);
  const fileRef = useRef(null);
  const [mode, setMode] = useState(null); // prescription | shop
  const [sheet, setSheet] = useState(null); // upload | review | checkout
  const [q, setQ] = useState('');
  const [medicines, setMedicines] = useState([]);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [cart, setCart] = useState({ items: [], subtotal: 0 });
  const [quote, setQuote] = useState(null);
  const [order, setOrder] = useState(null);
  const [tracking, setTracking] = useState(null);
  const [location, setLocation] = useState(null);
  const [locationState, setLocationState] = useState('locating');
  const [rxId, setRxId] = useState('');
  const [rxName, setRxName] = useState('');
  const [rxPreview, setRxPreview] = useState('');
  const [rxUploading, setRxUploading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [checkoutOpen, setCheckoutOpen] = useState(false);
  const [note, setNote] = useState('');
  const [history, setHistory] = useState([]);
  const [pharmacyStatus, setPharmacyStatus] = useState({ accepting_orders: true, enabled: true, pricing_complete: true, message: null });

  const getLocation = () => new Promise((resolve, reject) => {
    if (!navigator.geolocation) return reject(new Error('Location is not available on this device'));
    setLocationState('locating');
    navigator.geolocation.getCurrentPosition(
      (p) => {
        const next = { latitude: p.coords.latitude, longitude: p.coords.longitude, accuracy: p.coords.accuracy };
        setLocation(next);
        setLocationState('ready');
        resolve(next);
      },
      (e) => {
        setLocationState(e.code === 1 ? 'denied' : 'error');
        reject(new Error(e.code === 1 ? 'Allow location access to deliver to you' : 'Could not get your location'));
      },
      { enableHighAccuracy: true, timeout: 12000, maximumAge: 30000 },
    );
  });

  const loadCart = async () => {
    try {
      const { data } = await api.get('/pharmacy/cart');
      setCart(data.cart || { items: [], subtotal: 0 });
    } catch {}
  };

  const loadHistory = async () => {
    try {
      const { data } = await api.get('/pharmacy/orders');
      setHistory(data.orders || []);
    } catch {}
  };

  const searchMedicines = async (term = q, p = 1) => {
    try {
      const { data } = await api.get('/pharmacy/medicines', { params: { q: term, page: p, limit: 24 } });
      setMedicines(data.medicines || []);
      setPage(Number(data.page || 1));
      setPages(Number(data.pages || 1));
      setTotal(Number(data.total || 0));
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not load medicines');
    }
  };

  const loadPharmacyStatus = async () => {
    try {
      const { data } = await api.get('/pharmacy/status');
      setPharmacyStatus(data);
      return data;
    } catch {
      // Status is advisory. A transient status failure must never disable checkout.
      const fallback = {
        accepting_orders: true,
        enabled: true,
        pricing_complete: true,
        status_unknown: true,
        message: null,
      };
      setPharmacyStatus(fallback);
      return fallback;
    }
  };

  useEffect(() => {
    loadCart();
    loadHistory();
    loadPharmacyStatus();
    getLocation().catch(() => {});
  }, []);

  useEffect(() => {
    if (mode !== 'shop') return undefined;
    const t = setTimeout(() => searchMedicines(q, 1), 220);
    return () => clearTimeout(t);
  }, [q, mode]);

  useEffect(() => {
    if (!order?.id) return undefined;
    const timer = setInterval(async () => {
      try {
        const { data } = await api.get('/pharmacy/orders/' + order.id);
        setOrder(data.order);
        const active = ['ranger_assigned','ranger_arriving_pickup','ready_for_handover','picked_up','ranger_arriving_customer','out_for_delivery'].includes(data.order?.status);
        if (active) {
          const track = await api.get('/pharmacy/orders/' + order.id + '/tracking');
          setTracking(track.data.tracking);
        }
      } catch {}
    }, 4000);
    return () => clearInterval(timer);
  }, [order?.id]);

  const add = async (medicine) => {
    try {
      const { data } = await api.post('/pharmacy/cart/items', { medicine_id: medicine.id, quantity: 1 });
      setCart(data.cart);
      toast.success('Added to cart');
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not add'); }
  };

  const changeQty = async (id, qty) => {
    try {
      const { data } = await api.post('/pharmacy/cart/items', { medicine_id: id, quantity: qty });
      setCart(data.cart);
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not update quantity'); }
  };

  const remove = async (id) => {
    try {
      const { data } = await api.delete('/pharmacy/cart/items/' + id);
      setCart(data.cart);
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not remove item'); }
  };

  const chooseFile = (kind) => (kind === 'camera' ? cameraRef : fileRef).current?.click();

  const onPrescriptionFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const isImage = file.type?.startsWith('image/');
    if (!isImage && file.size > 10 * 1024 * 1024) return toast.error('PDF must be 10MB or smaller');
    if (isImage && file.size > 25 * 1024 * 1024) return toast.error('Photo must be 25MB or smaller');
    setRxUploading(true);
    try {
      const url = await uploadFile('prescriptions', file);
      const { data } = await api.post('/users/me/prescriptions', {
        doctor_name: 'Not provided',
        title: 'Pharmacy request',
        notes: note,
        image_url: url,
      });
      setRxId(data.prescription.id);
      setRxName(file.name);
      setRxPreview(file.type === 'application/pdf' ? '' : url);
      setSheet(null);
      toast.success('Prescription uploaded');
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message || 'Upload failed');
    } finally {
      setRxUploading(false);
      if (cameraRef.current) cameraRef.current.value = '';
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const sendPrescription = async () => {
    if (!rxId) return toast.error('Upload your prescription first');
    let gps = location;
    if (!gps) {
      try { gps = await getLocation(); } catch (e) { return toast.error(e.message); }
    }
    setLoading(true);
    try {
      const { data } = await api.post('/pharmacy/prescription-orders', {
        delivery_address: 'Current location',
        prescription_id: rxId,
        latitude: gps.latitude,
        longitude: gps.longitude,
        notes: note || undefined,
      });
      setOrder(data.order);
      setMode(null);
      await loadHistory();
      toast.success('Prescription sent to nearby pharmacies');
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not send prescription'); }
    finally { setLoading(false); }
  };

  const beginShopCheckout = async () => {
    let gps = location;
    if (!gps) {
      try { gps = await getLocation(); } catch (e) { return toast.error(e.message); }
    }
    try {
      const { data: quoteData } = await api.post('/pharmacy/quote', {
        delivery_address: 'Current location',
        latitude: gps.latitude,
        longitude: gps.longitude,
      });
      setQuote(quoteData.quote);
      setSheet('checkout');
      // Availability is advisory for the review screen. Never make a cart
      // review depend on a secondary status request.
      loadPharmacyStatus();
    } catch (e) {
      const statusCode = e.response?.status;
      if (statusCode === 503) {
        await loadPharmacyStatus();
        // Review remains available while new intake is offline. Never invent a
        // customer total when the server cannot provide an authoritative quote.
        setQuote({
          subtotal: Number(cart.subtotal || 0),
          delivery_fee: null,
          platform_fee: null,
          tax: null,
          total: null,
        });
        setSheet('checkout');
        toast.message('Review is ready. Final delivery availability will be confirmed when you place the order.');
        return;
      }
      toast.error(e.response?.data?.detail || 'Checkout could not be prepared');
    }
  };

  const payOrder = async (targetOrder) => {
    setLoading(true);
    try {
      const payment = await api.post('/pharmacy/orders/' + targetOrder.id + '/payment/order');
      setCheckoutOpen(true);
      await openRazorpayCheckout({
        checkout: payment.data.checkout,
        description: 'RESQLY Pharmacy ' + targetOrder.id.slice(0, 8),
        onSuccess: async (response) => {
          try {
            const verified = await api.post('/pharmacy/orders/payment/verify', { order_id: targetOrder.id, ...response });
            setOrder(verified.data.order);
            setCheckoutOpen(false);
            await loadHistory();
            toast.success('Payment successful');
          } catch (e) {
            setCheckoutOpen(false);
            toast.error(e.response?.data?.detail || 'Payment verification failed');
          }
        },
        onDismiss: () => setCheckoutOpen(false),
        onFailed: () => { setCheckoutOpen(false); toast.error('Payment failed — retry when ready'); },
      });
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not start payment');
    } finally { setLoading(false); }
  };

  const placeShopOrder = async () => {
    let gps = location;
    if (!gps) {
      try { gps = await getLocation(); } catch (e) { return toast.error(e.message); }
    }
    setLoading(true);
    try {
      // Do not gate checkout on a separate status request. That request can be
      // stale between review and placement and was causing false "offline"
      // messages. The order endpoint is the authoritative availability check.
      const { data } = await api.post('/pharmacy/orders', {
        delivery_address: 'Current location',
        latitude: gps.latitude,
        longitude: gps.longitude,
      });
      setOrder(data.order);
      setSheet(null);
      await payOrder(data.order);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not place order');
    } finally { setLoading(false); }
  };

  const retryPharmacy = async () => {
    if (!order) return;
    try {
      const { data } = await api.post('/pharmacy/orders/' + order.id + '/retry-pharmacy');
      setOrder(data.order);
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not retry'); }
  };

  const retryRanger = async () => {
    if (!order) return;
    try {
      const { data } = await api.post('/pharmacy/orders/' + order.id + '/retry-ranger');
      setOrder(data.order);
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not retry Ranger'); }
  };

  const cancelOrder = async () => {
    if (!order) return;
    try {
      const { data } = await api.post('/pharmacy/orders/' + order.id + '/cancel');
      setOrder(data.order);
      loadHistory();
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not cancel'); }
  };

  const cartCount = useMemo(() => cart.items?.reduce((n, x) => n + Number(x.quantity || 0), 0) || 0, [cart.items]);
  const activeTracking = order && ['ranger_assigned','ranger_arriving_pickup','ready_for_handover','picked_up','ranger_arriving_customer','out_for_delivery'].includes(order.status);
  const quoteReady = order?.order_type === 'prescription' && order.status === 'pharmacy_accepted' && order.pricing_snapshot?.quote_status === 'ready';

  if (order) {
    return (
      <MobileShell title="Pharmacy">
        <div className="min-h-screen bg-[#f7f7f5] pb-10">
          <div className="px-5 pt-3 flex items-center gap-3">
            <button onClick={() => setOrder(null)} className="w-9 h-9 rounded-full bg-white border flex items-center justify-center"><ArrowLeft className="w-4 h-4" /></button>
            <div><div className="text-[10px] uppercase tracking-widest text-slate-400">Pharmacy</div><div className="font-black">Order #{order.id.slice(0,8)}</div></div>
          </div>
          <OrderStatus order={order} />

          {activeTracking && tracking && (
            <div className="mx-5 mt-4 rounded-3xl overflow-hidden bg-white border shadow-sm">
              <ProviderTrackingMap tracking={tracking} />
              <div className="p-4">
                <div className="font-bold flex items-center gap-2"><Navigation className="w-4 h-4 text-emerald-600" />{tracking.eta_minutes != null ? 'Arriving in ' + tracking.eta_minutes + ' min' : 'Ranger location updating'}</div>
                <div className="text-xs text-slate-500 mt-1">{tracking.distance_km != null ? tracking.distance_km + ' km away' : 'Live location unavailable'}</div>
              </div>
            </div>
          )}

          {quoteReady && (
            <div className="mx-5 mt-4 p-5 rounded-3xl bg-white border border-emerald-100 shadow-sm">
              <div className="font-black text-emerald-700">Your pharmacy quote is ready</div>
              <div className="mt-3 space-y-2 text-sm">
                <Line l="Medicines" v={order.pricing_snapshot.subtotal} />
                <div className="rounded-2xl bg-emerald-50 border border-emerald-100 p-3 text-xs text-emerald-900">
                  <div className="font-bold">{order.pricing_snapshot.all_medicines_available ? 'All prescription medicines reported available' : `${order.pricing_snapshot.available_medicine_count || 0} medicines reported available`}</div>
                  <div className="mt-1">RESQLY ranked pharmacies by medicine coverage, then quotation price, then backend-calculated ETA.</div>
                </div>
                <Line l="Delivery" v={order.pricing_snapshot.delivery_fee} />
                <Line l="Platform fee" v={order.pricing_snapshot.platform_fee} />
                <Line l="Tax" v={order.pricing_snapshot.tax} />
                <div className="border-t pt-3 flex justify-between font-black"><span>Total</span><span>₹{money(order.pricing_snapshot.total)}</span></div>
              </div>
              <Button onClick={() => payOrder(order)} disabled={loading || checkoutOpen} className="w-full mt-4 rounded-2xl bg-[#171717]">Pay ₹{money(order.pricing_snapshot.total)}</Button>
            </div>
          )}

          {['payment_pending','payment_failed'].includes(order.status) && (
            <div className="mx-5 mt-4 rounded-3xl bg-amber-50 p-4 border border-amber-200">
              <div className="font-bold text-amber-900">Payment needs attention</div>
              <div className="text-xs text-amber-800 mt-1">Your cart/order is safe. Retry payment to continue.</div>
              <Button onClick={() => payOrder(order)} className="w-full mt-3 rounded-2xl">Retry payment</Button>
            </div>
          )}

          {order.status === 'pharmacy_unavailable' && <div className="mx-5 mt-4"><Button onClick={retryPharmacy} className="w-full rounded-2xl"><RefreshCw className="w-4 h-4 mr-2" /> Try another pharmacy</Button></div>}
          {order.status === 'delivery_failed' && <div className="mx-5 mt-4"><Button onClick={retryRanger} className="w-full rounded-2xl"><RefreshCw className="w-4 h-4 mr-2" /> Find another Ranger</Button></div>}

          {order.delivery_otp && ['ranger_arriving_customer','out_for_delivery'].includes(order.status) && (
            <div className="mx-5 mt-4 rounded-3xl bg-[#171717] p-5 text-white">
              <div className="text-[10px] uppercase tracking-widest text-white/50">Delivery OTP</div>
              <div className="text-4xl font-black tracking-[.25em] mt-1">{order.delivery_otp}</div>
              <div className="text-xs text-white/60 mt-2">Share only with the Ranger when your order reaches you.</div>
            </div>
          )}

          {!['completed','delivered','cancelled','refunded','out_for_delivery','ranger_arriving_customer','picked_up'].includes(order.status) && (
            <div className="mx-5 mt-5"><Button variant="outline" onClick={cancelOrder} className="w-full rounded-2xl text-red-700 border-red-200">Cancel order</Button></div>
          )}

          {['completed','cancelled','refunded'].includes(order.status) && (
            <div className="mx-5 mt-5"><Button className="w-full rounded-2xl" onClick={() => { setOrder(null); setTracking(null); setMode(null); loadCart(); }}>Order another</Button></div>
          )}
        </div>
      </MobileShell>
    );
  }

  return (
    <MobileShell title="Pharmacy">
      <div className="min-h-screen bg-[#f7f7f5] pb-24">
        <input ref={cameraRef} type="file" accept="image/*" capture="environment" onChange={onPrescriptionFile} className="hidden" />
        <input ref={fileRef} type="file" accept="image/*,application/pdf" onChange={onPrescriptionFile} className="hidden" />

        <div className="px-5 pt-3">
          <button onClick={() => window.history.back()} className="text-sm text-slate-500 inline-flex items-center gap-1"><ArrowLeft className="w-4 h-4" /> Back</button>
        </div>

        <AnimatePresence mode="wait">
          {!mode && (
            <motion.div key="home" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="px-5 pt-5 space-y-5">
              <div className="rounded-[30px] bg-gradient-to-br from-[#1e8b62] via-[#24aa76] to-[#65d59d] p-6 text-white relative overflow-hidden">
                <Sparkles className="absolute right-6 top-5 w-7 h-7 text-white/30" />
                <div className="text-[10px] uppercase tracking-[.22em] font-bold text-white/70">RESQLY PHARMACY</div>
                <div className="text-3xl font-black tracking-tight mt-2">What do you need?</div>
                <div className="text-sm text-white/85 mt-2">Two simple ways to get medicines to where you are.</div>
                <div className="flex items-center gap-2 mt-5 text-xs font-semibold"><MapPin className="w-4 h-4" />{locationState === 'ready' ? 'Delivering to your current location' : 'Finding your location…'}</div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <ActionCard icon={<FileText />} title="Upload prescription" body="No medicine names needed." tone="blue" onClick={() => setMode('prescription')} />
                <ActionCard icon={<ShoppingBag />} title="Shop medicines" body="Search verified non-prescription medicines." tone="green" onClick={() => setMode('shop')} />
              </div>

              {locationState !== 'ready' && (
                <div className="rounded-2xl bg-amber-50 border border-amber-200 p-4 flex gap-3">
                  <AlertCircle className="w-5 h-5 text-amber-700" />
                  <div><div className="font-bold text-amber-900">Location access needed</div><div className="text-xs text-amber-800 mt-1">Allow location so Rangers know exactly where to deliver.</div></div>
                </div>
              )}
            </motion.div>
          )}

          {mode === 'prescription' && (
            <motion.div key="rx" initial={{ opacity: 0, x: 16 }} animate={{ opacity: 1, x: 0 }} className="px-5 pt-5 space-y-4">
              <button onClick={() => setMode(null)} className="text-sm text-slate-500 inline-flex items-center gap-1"><ArrowLeft className="w-4 h-4" /> Pharmacy</button>
              <div><div className="text-[10px] uppercase tracking-widest text-blue-700 font-bold">Prescription</div><div className="text-2xl font-black mt-1">Just send us the prescription.</div><div className="text-sm text-slate-500 mt-1">We’ll find a nearby verified pharmacy and ask you to pay only after it confirms the order.</div></div>

              {!rxName ? (
                <button onClick={() => setSheet('upload')} className="w-full rounded-[28px] border-2 border-dashed border-blue-200 bg-white p-8 text-center shadow-sm">
                  <div className="w-14 h-14 rounded-2xl bg-blue-50 text-blue-700 flex items-center justify-center mx-auto"><Upload /></div>
                  <div className="font-black mt-4">Add prescription</div>
                  <div className="text-xs text-slate-500 mt-1">Camera or Files & Photos</div>
                </button>
              ) : (
                <div className="rounded-[28px] bg-white border p-4 flex items-center gap-3">
                  <div className="w-16 h-16 rounded-2xl bg-slate-50 flex items-center justify-center overflow-hidden">{rxPreview ? <img src={rxPreview} alt="Prescription" className="w-full h-full object-cover" /> : <FileText className="text-slate-400" />}</div>
                  <div className="flex-1 min-w-0"><div className="font-bold truncate">{rxName}</div><div className="text-xs text-emerald-700 mt-1">Uploaded</div></div>
                  <button onClick={() => setSheet('upload')} className="text-xs font-bold text-blue-700">Replace</button>
                </div>
              )}

              <div className="rounded-[24px] bg-white border p-4">
                <div className="font-bold text-sm">Optional note to pharmacy</div>
                <textarea value={note} onChange={(e) => setNote(e.target.value)} placeholder="Anything they should know?" className="w-full mt-2 border rounded-2xl p-3 text-sm min-h-20" />
              </div>

              <div className="rounded-[24px] bg-[#171717] text-white p-4 flex items-center gap-3">
                <MapPin className="w-5 h-5 text-emerald-300" />
                <div className="flex-1"><div className="font-bold text-sm">Deliver to current location</div><div className="text-xs text-white/60">No address entry needed.</div></div>
                {locationState === 'ready' ? <Check className="text-emerald-300" /> : <Clock3 className="text-white/40" />}
              </div>

              <Button onClick={sendPrescription} disabled={!rxId || rxUploading || loading || locationState !== 'ready'} className="w-full h-12 rounded-2xl bg-[#171717]">{loading ? 'Finding pharmacy…' : 'Find a pharmacy'} <ChevronRight className="w-4 h-4 ml-1" /></Button>
            </motion.div>
          )}

          {mode === 'shop' && (
            <motion.div key="shop" initial={{ opacity: 0, x: 16 }} animate={{ opacity: 1, x: 0 }} className="pt-4">
              <div className="px-5">
                <button onClick={() => setMode(null)} className="text-sm text-slate-500 inline-flex items-center gap-1"><ArrowLeft className="w-4 h-4" /> Pharmacy</button>
                <div className="mt-4 text-[10px] uppercase tracking-widest text-emerald-700 font-bold">Shop medicines</div>
                <div className="text-2xl font-black mt-1">Search by medicine, brand or salt.</div>
                <div className="relative mt-4"><Search className="absolute left-4 top-3.5 w-4 h-4 text-slate-400" /><Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search medicines" className="pl-11 h-12 rounded-2xl bg-white" /></div>
                <div className="text-xs text-slate-500 mt-3">{total.toLocaleString('en-IN')} verified non-prescription medicines</div>
              </div>

              <div className="px-5 mt-4">
                {medicines.length ? (
                  <div className="grid grid-cols-2 gap-3">
                    {medicines.map((m) => <MedicineCard key={m.id} medicine={m} onAdd={add} />)}
                  </div>
                ) : <div className="rounded-3xl bg-white border p-8 text-center"><Pill className="mx-auto text-slate-300" /><div className="font-bold mt-3">{q ? 'No matching medicine' : 'No verified OTC medicines yet'}</div><div className="text-xs text-slate-500 mt-1">Try another search or check back after Admin updates the catalogue.</div></div>}
              </div>

              {pages > 1 && <div className="px-5 flex justify-between items-center mt-5"><button disabled={page<=1} onClick={() => searchMedicines(q, page-1)} className="px-3 py-2 rounded-full bg-white border disabled:opacity-40">Previous</button><span className="text-xs text-slate-500">Page {page} of {pages}</span><button disabled={page>=pages} onClick={() => searchMedicines(q, page+1)} className="px-3 py-2 rounded-full bg-white border disabled:opacity-40">Next</button></div>}
            </motion.div>
          )}
        </AnimatePresence>

        {mode === 'shop' && cart.items?.length > 0 && (
          <div className="fixed bottom-16 left-1/2 -translate-x-1/2 z-30 w-[calc(100%-40px)] max-w-[440px]">
            <button onClick={beginShopCheckout} className="w-full rounded-2xl bg-[#171717] text-white px-4 py-3 shadow-2xl flex items-center gap-3">
              <div className="w-9 h-9 rounded-xl bg-white/10 flex items-center justify-center"><ShoppingBag className="w-4 h-4" /></div>
              <div className="flex-1 text-left"><div className="font-black text-sm">{cartCount} items</div><div className="text-[11px] text-white/60">Review order</div></div>
              <div className="font-black">₹{money(cart.subtotal)}</div><ChevronRight className="w-4 h-4" />
            </button>
          </div>
        )}

        {sheet === 'upload' && <BottomSheet title="Add prescription" onClose={() => setSheet(null)}>
          <div className="grid grid-cols-2 gap-3">
            <UploadOption icon={<Camera />} title="Camera" body="Take a clear photo" onClick={() => chooseFile('camera')} />
            <UploadOption icon={<ImageIcon />} title="Files & Photos" body="Choose from your device" onClick={() => chooseFile('file')} />
          </div>
        </BottomSheet>}

        {sheet === 'checkout' && <BottomSheet title="Review order" onClose={() => setSheet(null)}>
          {(cart.items || []).map((item) => <div key={item.medicine.id} className="flex items-center gap-3 py-2">
            <ProductImage medicine={item.medicine} small /><div className="flex-1 min-w-0"><div className="font-bold text-sm truncate">{item.medicine.name}</div><div className="text-xs text-slate-500">Qty {item.quantity}</div></div><div className="font-black text-sm">₹{money(item.line_total)}</div>
          </div>)}
          <div className="border-t mt-3 pt-3 space-y-2">
            <Line l="Medicines" v={cart.subtotal} />
            <Line l="Delivery" v={quote?.delivery_fee} />
            <Line l="Platform fee" v={quote?.platform_fee} />
            <Line l="Tax" v={quote?.tax} />
            <div className="border-t pt-3 flex justify-between font-black">
              <span>Total</span><span>{quote?.total != null ? '₹' + money(quote.total) : '—'}</span>
            </div>
          </div>
          {!pharmacyStatus.accepting_orders && quote?.total != null && (
            <div className="rounded-2xl bg-amber-50 border border-amber-200 p-3 mt-4 text-xs text-amber-900">
              <div className="font-bold">New Pharmacy orders are paused</div>
              <div className="mt-1">{pharmacyStatus.message || 'Your order can be reviewed, but new intake is temporarily paused.'}</div>
            </div>
          )}
          <div className="rounded-2xl bg-slate-50 p-3 mt-4 text-xs text-slate-600 flex gap-2"><MapPin className="w-4 h-4 text-emerald-600 mt-0.5" /> Ranger will deliver to your current GPS location.</div>
          <Button onClick={placeShopOrder} disabled={loading || quote?.total == null} className="w-full mt-4 rounded-2xl bg-[#171717]">
            {loading ? 'Starting payment…' : 'Pay securely'}
          </Button>
        </BottomSheet>}
      </div>
    </MobileShell>
  );
}

function ActionCard({ icon, title, body, tone, onClick }) {
  const bg = tone === 'blue' ? 'bg-blue-50 border-blue-100' : 'bg-emerald-50 border-emerald-100';
  return <motion.button whileTap={{ scale: .98 }} onClick={onClick} className={'text-left rounded-[26px] border p-4 bg-white shadow-sm ' + bg}><div className="w-11 h-11 rounded-2xl bg-white flex items-center justify-center text-slate-700">{icon}</div><div className="font-black mt-4">{title}</div><div className="text-xs text-slate-500 mt-1 leading-4">{body}</div><div className="mt-4 text-xs font-bold inline-flex items-center gap-1">Continue <ChevronRight className="w-3 h-3" /></div></motion.button>;
}

function MedicineCard({ medicine, onAdd }) {
  return <motion.div whileHover={{ y: -2 }} className="rounded-[24px] bg-white border border-slate-200 overflow-hidden shadow-sm">
    <ProductImage medicine={medicine} />
    <div className="p-3"><div className="text-[10px] uppercase tracking-wide text-slate-400 truncate">{medicine.brand_name || medicine.generic_name || 'Medicine'}</div><div className="font-bold text-sm mt-1 leading-5">{medicine.name}</div><div className="text-[11px] text-slate-500 mt-1 line-clamp-2">{medicine.composition || medicine.strength || medicine.pack_size}</div><div className="flex items-end justify-between mt-3"><div><div className="font-black">₹{money(medicine.mrp)}</div><div className="text-[10px] text-slate-400">MRP</div></div><button onClick={() => onAdd(medicine)} className="w-9 h-9 rounded-xl bg-emerald-600 text-white flex items-center justify-center"><Plus className="w-4 h-4" /></button></div></div>
  </motion.div>;
}

function ProductImage({ medicine, small = false }) {
  return <div className={(small ? 'w-12 h-12 rounded-xl' : 'h-36 rounded-t-[24px]') + ' bg-gradient-to-br from-slate-50 to-emerald-50 flex items-center justify-center overflow-hidden'}>
    {medicine?.image_url ? <img src={medicine.image_url} alt={medicine.name || 'Medicine'} className="w-full h-full object-contain" onError={(e) => { e.currentTarget.style.display = 'none'; }} /> : <Pill className="text-emerald-600" />}
  </div>;
}

function BottomSheet({ title, children, onClose }) {
  return <div className="fixed inset-0 z-50 bg-slate-950/40 flex items-end justify-center" onClick={onClose}>
    <motion.div initial={{ y: 40, opacity: 0 }} animate={{ y: 0, opacity: 1 }} className="w-full max-w-[480px] max-h-[88vh] overflow-y-auto bg-white rounded-t-[32px] p-5" onClick={(e) => e.stopPropagation()}>
      <div className="w-10 h-1 bg-slate-200 rounded-full mx-auto mb-4" /><div className="flex items-center justify-between mb-4"><h3 className="font-black text-lg">{title}</h3><button onClick={onClose} className="w-9 h-9 rounded-full bg-slate-100 flex items-center justify-center"><X className="w-4 h-4" /></button></div>{children}
    </motion.div>
  </div>;
}

function UploadOption({ icon, title, body, onClick }) {
  return <button onClick={onClick} className="text-left rounded-3xl border bg-slate-50 p-5"><div className="w-11 h-11 rounded-2xl bg-white flex items-center justify-center text-blue-700 shadow-sm">{icon}</div><div className="font-bold mt-4">{title}</div><div className="text-xs text-slate-500 mt-1">{body}</div></button>;
}

function Line({ l, v }) {
  return <div className="flex justify-between gap-4 text-sm">
    <span className="text-slate-600">{l}</span>
    <span className="font-semibold">{v == null ? '—' : '₹' + money(v)}</span>
  </div>;
}

function OrderStatus({ order }) {
  const i = Math.max(0, STEPS.indexOf(order.status));
  return <div className="mx-5 mt-5 rounded-3xl bg-[#171717] text-white p-5">
    <div className="flex justify-between text-xs"><span className="text-white/50">{order.order_type === 'prescription' ? 'Prescription order' : 'Medicine order'}</span><span className="text-emerald-300 font-bold">{STATUS[order.status] || order.status}</span></div>
    <div className="text-3xl font-black mt-2">{order.order_type === 'prescription' && !order.pricing_snapshot?.total ? 'Waiting for pharmacy quote' : '₹' + money(order.pricing_snapshot?.total)}</div>
    <div className="mt-4 h-2 rounded-full bg-white/10 overflow-hidden"><div className="h-full bg-emerald-400" style={{ width: (((i + 1) / STEPS.length) * 100) + '%' }} /></div>
    <div className="text-[10px] text-white/50 mt-2">Step {Math.min(i + 1, STEPS.length)} of {STEPS.length}</div>
  </div>;
}
